import { createRouter, createWebHistory, type RouteRecordNormalized } from 'vue-router';
import guestRoutes from './guest';
import berechnungsgrundlagen_routes from './berechnungsgrundlagen_routes';
import system_routes from './system_routes';
import datenimport_routes from './datenimport_routes';
import berechnungen_routes from './berechnungen_routes';
import auswertungen_routes from './auswertungen_routes';
import { authUserHasRole, useAuthUser } from '@/helpers/globals';
import { appUrl } from '@/helpers/api';
import { nextTick } from 'vue';

import HomeView from '@/views/auth/HomeView.vue';
import NotFoundView from '@/views/NotFoundView.vue';
import type { ROLES } from '@/models/Rolle';

/**
 * Possible meta data values:
 * - requiresAuth: true if the route needs an authenticated user
 * - roles: array of allowed roles, if a specific role is needed
 */

const rootRoutes = [
    {
        path: '/',
        name: 'home',
        component: HomeView,
        meta: {
            requiresAuth: true,
        },
    },
];

const notFoundRoute = { path: '/:pathMatch(.*)*', name: 'NotFound', component: NotFoundView };

const routes = [
    ...guestRoutes,
    ...rootRoutes,
    ...berechnungsgrundlagen_routes,
    ...system_routes,
    ...datenimport_routes,
    ...berechnungen_routes,
    ...auswertungen_routes,
    notFoundRoute];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
});

router.beforeEach((to, from) => {
    document.body.style.cursor = 'wait';

    // Auth requiring routes need an auth user:
    if (to.matched.some((rec) => rec.meta.requiresAuth)) {
        if (!useAuthUser()) {
            const fromUrl = window.location.href;
            window.location.href = appUrl('/login', { url: encodeURIComponent(fromUrl) });
            return;
        } else {
            // Check if the route needs specific privileges
            const allRoutesAllowed = to.matched.every((routeRec) => {
                // return true;
                if (routeRec.meta.roles && (routeRec.meta.roles as (keyof typeof ROLES)[]).length > 0) {
                    for (const requiredRole of routeRec.meta.roles as (keyof typeof ROLES)[]) {
                        if (authUserHasRole(requiredRole)) return true;
                    }
                    // no role matches
                    return false;
                } else {
                    // no roles needed
                    return true;
                }
            });
            if (allRoutesAllowed) {
                return;
            } else {
                // no access, abort, show 404
                return { name: 'NotFound' };
            }
        }
    } else {
        // no auth needed - guest routes are allowed for everyone
        return;
    }
});

router.afterEach((to, from) => {
    document.body.style.cursor = 'initial';

    // see https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609 why we use
    // nextTick here:
    nextTick(() => {
        // Update the page's title, based on the Route's meta data, if applicable:
        let title = (to.meta && to.meta.title) || null;
        if (title instanceof Function) {
            title = title(to, from);
        }
        document.title = (title || '') + (title ? ' - ECO₂Stat' : 'ECO₂Stat');
    });
});

export function routerTitle(route: RouteRecordNormalized): string {
    const title = route.meta?.title || null;
    if (title instanceof Function) {
        return title();
    } else if (typeof title === 'string') {
        return title || '(unknown)';
    } else {
        return '(unknown)';
    }
}

export default router;
